import { NgIf } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ConnectDashboardService } from './connect-dashboard.service';
import { ConnectIconComponent } from './connect-icon.component';
import { ConnectRequest, requestTitle } from './connect-dashboard.util';

export interface ConnectRequestDialogData {
  groupId: string;
  request: ConnectRequest;
}

type Step = 'view' | 'declining' | 'drafting' | 'review' | 'sending';

/**
 * What the practice's billing office on MedicAI Connect asked for a patient (a letter of motivation). The doctor
 * declines with a reason, or has CURA draft the letter from the patient's record, edits it and accepts: the letter is
 * put on the practice letterhead, filed on the patient's timeline here and sent back to Connect. Closes with 'answered'.
 */
@Component({
  selector: 'app-connect-request-dialog',
  standalone: true,
  imports: [NgIf, FormsModule, ConnectIconComponent],
  templateUrl: './connect-request-dialog.component.html',
  styleUrls: ['./connect-request-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ConnectRequestDialogComponent {
  step: Step = 'view';
  reason = '';
  letter = '';
  error: string | null = null;
  readonly title: string;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ConnectRequestDialogData,
    private dialogRef: MatDialogRef<ConnectRequestDialogComponent, 'answered'>,
    private connect: ConnectDashboardService,
  ) {
    this.title = requestTitle(data.request);
  }

  get request(): ConnectRequest {
    return this.data.request;
  }

  /** The patient here; a request about someone who is not this practice's patient cannot be drafted. */
  get patientHere(): string | null {
    return this.request.patient.clinicalPatientId;
  }

  close(): void {
    this.dialogRef.close();
  }

  decline(): void {
    if (!this.reason.trim()) return;
    this.step = 'sending';
    this.error = null;
    this.connect.declineRequest(this.data.groupId, this.request.id, this.reason.trim()).subscribe({
      next: () => this.dialogRef.close('answered'),
      error: (err: HttpErrorResponse) => this.fail(err, 'declining'),
    });
  }

  draft(): void {
    const patientId = this.patientHere;
    if (!patientId) return;
    this.step = 'drafting';
    this.error = null;
    const { reason, claimNumber } = this.request.details;
    this.connect.draftRequest(this.data.groupId, this.request.id, { patientId, reason, claimNumber }).subscribe({
      next: ({ text }) => {
        this.letter = text;
        this.step = 'review';
      },
      error: (err: HttpErrorResponse) => this.fail(err, 'view'),
    });
  }

  accept(): void {
    const patientId = this.patientHere;
    if (!patientId || !this.letter.trim()) return;
    this.step = 'sending';
    this.error = null;
    this.connect.acceptRequest(this.data.groupId, this.request.id, { patientId, text: this.letter }).subscribe({
      next: () => this.dialogRef.close('answered'),
      error: (err: HttpErrorResponse) => this.fail(err, 'review'),
    });
  }

  private fail(err: HttpErrorResponse, back: Step): void {
    this.error = err.error?.message || 'Something went wrong. Please try again.';
    this.step = back;
  }
}
